import React, { useLayoutEffect, useRef, useState } from 'react';

import { usePlotArea } from 'recharts';

import { type TrendPoint, placeDailyMarginLabel } from './dailyMarginPlacement';

type Props = {
  cx?: number;
  cy?: number;
  index?: number;
  lastIndex: number;
  value: unknown;
  date: string;
  color: string;
  points?: ReadonlyArray<TrendPoint>;
};

export default function DailyMarginDot({ cx, cy, index, lastIndex, value, date, color, points = [] }: Props) {
  const plot = usePlotArea();
  const text = useRef<SVGGElement>(null);
  const [width, setWidth] = useState(88);
  const valid = index === lastIndex && typeof value === 'number' && Number.isFinite(value) && cx != null && cy != null;
  const amount = valid
    ? new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).format(value as number)
    : '';
  const day = valid
    ? new Intl.DateTimeFormat('en-AU', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(`${date}T00:00:00Z`))
    : '';
  useLayoutEffect(() => {
    const measure = () => {
      const box = text.current?.getBBox();
      if (box && box.width > 0) setWidth(Math.ceil(box.width) + 10);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (text.current) observer?.observe(text.current);
    return () => observer?.disconnect();
  }, [amount, day]);
  // Never move backwards over missing data or replace it with zero.
  if (!valid) return <g />;
  const end = { x: cx as number, y: cy as number };
  const box = placeDailyMarginLabel(
    points,
    plot ?? { x: 0, y: 0, width: end.x + 20, height: end.y + 60 },
    { width, height: 36 },
    end,
  );
  const target = {
    x: Math.max(box.x, Math.min(end.x, box.x + box.width)),
    y: Math.max(box.y, Math.min(end.y, box.y + box.height)),
  };
  const leader = Math.hypot(target.x - end.x, target.y - end.y) > 24;
  return (
    <g className="lp-daily-margin-dot" aria-label={`${day}: ${amount}`} data-label-collisions={box.collisions}>
      {leader && (
        <line
          className="lp-daily-margin-leader"
          x1={end.x}
          y1={end.y}
          x2={target.x}
          y2={target.y}
          stroke={color}
          strokeWidth={1}
          strokeDasharray="2 3"
          opacity={0.65}
        />
      )}
      <circle cx={cx} cy={cy} r={4} fill={color} stroke="var(--lp-panel, #fff)" strokeWidth={1.5} />
      <g className="lp-daily-margin-label" transform={`translate(${box.x},${box.y})`}>
        {box.collisions > 0 && <rect width={box.width} height={box.height} rx={3} fill="var(--lp-panel, #fff)" />}
        <g
          ref={text}
          textAnchor="end"
          stroke="var(--lp-panel, #fff)"
          strokeWidth={2}
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          <text x={box.width - 5} y={14} fill="var(--lp-ink, #0F0C08)" fontSize={12} fontWeight={700}>
            {amount}
          </text>
          <text x={box.width - 5} y={29} fill="var(--lp-muted, #0F464F)" fontSize={10}>
            {day}
          </text>
        </g>
      </g>
    </g>
  );
}
